import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    ArrowLeft,
    BookOpen,
    ShoppingBag,
    CheckCircle2,
    Quote,
    Compass,
    Heart,
} from "lucide-react";
import SEO, {
    organizationJsonLd,
    websiteJsonLd,
    personJsonLd,
} from "@/components/common/SEO";
import Stamp from "@/components/common/Stamp";
import ShareBar from "@/components/common/ShareBar";
import TestimoniosSection from "@/components/common/TestimoniosSection";
import { LIBRO } from "@/data/staticContent";
import { SITE_URL } from "@/lib/api";

const bookJsonLd = {
    "@context": "https://schema.org",
    "@type": "Book",
    "@id": `${SITE_URL}/libro#book`,
    name: LIBRO.title,
    alternativeHeadline: LIBRO.subtitle,
    author: {
        "@type": "Person",
        name: LIBRO.author,
        alternateName: ["NiTanAdicto", "Nelson González Rodríguez"],
        url: `${SITE_URL}/nelson-gonzalez-rodriguez`,
    },
    inLanguage: "es-CL",
    bookFormat: "https://schema.org/EBook",
    numberOfPages: 141,
    publisher: { "@type": "Organization", name: "El Mapa Inverso", url: SITE_URL },
    datePublished: "2026",
    image: LIBRO.coverUrl,
    description:
        "El libro de Nelson González sobre cómo convertir la autodestrucción en un camino de vida. 16 capítulos en 4 partes. Sobriedad con sentido, sin moralina.",
    keywords:
        "libro adicción, recuperación, sobriedad consciente, Nelson González, NiTanAdicto, mapa inverso, autodestrucción, libro digital Chile",
    audience: {
        "@type": "Audience",
        audienceType: "Personas con adicción, en recuperación, y familiares/parejas",
    },
    workExample: [
        {
            "@type": "Book",
            "@id": `${SITE_URL}/libro#ebook-pdf`,
            bookEdition: "Primera edición digital",
            bookFormat: "https://schema.org/EBook",
            encodingFormat: "application/pdf",
            isAccessibleForFree: true,
            inLanguage: "es",
            potentialAction: {
                "@type": "ReadAction",
                target: `${SITE_URL}/descargar`,
                expectsAcceptanceOf: {
                    "@type": "Offer",
                    priceCurrency: "CLP",
                    price: 0,
                    eligibleRegion: { "@type": "Country", name: "CL" },
                },
            },
        },
        {
            "@type": "Book",
            "@id": `${SITE_URL}/libro#ebook-epub`,
            bookEdition: "Primera edición digital",
            bookFormat: "https://schema.org/EBook",
            encodingFormat: "application/epub+zip",
            isAccessibleForFree: true,
            inLanguage: "es",
            potentialAction: {
                "@type": "ReadAction",
                target: `${SITE_URL}/descargar`,
            },
        },
    ],
    // hasPart: los 16 capítulos como Chapter cada uno, con position para el orden
    hasPart: LIBRO.chapters.flatMap((part) =>
        part.items.map((ch) => ({
            "@type": "Chapter",
            name: ch.title,
            position: ch.n,
            isPartOf: { "@id": `${SITE_URL}/libro#book` },
        })),
    ),
    isAccessibleForFree: true,
    about: [
        { "@type": "Thing", name: "Adicción" },
        { "@type": "Thing", name: "Sobriedad consciente" },
        { "@type": "Thing", name: "Recuperación" },
        { "@type": "Thing", name: "Autodestrucción" },
    ],
    offers: {
        "@type": "Offer",
        priceCurrency: "CLP",
        price: 0,
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}/descargar`,
        priceValidUntil: "2027-12-31",
        seller: { "@type": "Organization", name: "El Mapa Inverso" },
    },
};

// Product schema en paralelo — Google SÍ muestra rich snippet de Product (precio + disponibilidad)
const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE_URL}/libro#product`,
    name: `${LIBRO.title} · libro digital · descarga gratuita`,
    description:
        "Libro digital de Nelson González Rodríguez. 16 capítulos crudos sobre cómo convertir la autodestrucción en un camino de vida. Sobriedad con sentido, sin moralina. Formato PDF + EPUB. Descarga 100% gratuita, sin condiciones.",
    image: [LIBRO.coverUrl],
    brand: { "@type": "Brand", name: "El Mapa Inverso" },
    sku: "EMI-2026-001",
    category: "Libros digitales / Recuperación de adicciones",
    isRelatedTo: { "@id": `${SITE_URL}/libro#book` },
    offers: {
        "@type": "Offer",
        priceCurrency: "CLP",
        price: 0,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        url: `${SITE_URL}/descargar`,
        seller: {
            "@type": "Organization",
            name: "El Mapa Inverso",
            url: SITE_URL,
        },
    },
};

const libroFaqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "¿Cuánto cuesta el libro?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "El libro es 100% gratis. Sin costo, sin condiciones, sin letra chica. Solo dejas tu email para recibir el link de descarga.",
            },
        },
        {
            "@type": "Question",
            name: "¿Cómo recibo el libro tras descargarlo?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Al llenar el formulario recibes al instante un email con el link seguro de descarga. Compatible con cualquier dispositivo (celular, tablet, computador, lector de e-books).",
            },
        },
        {
            "@type": "Question",
            name: "¿Qué formato tiene el libro?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Edición digital — PDF (141 páginas) + EPUB. Sin DRM agresivo, para uso personal. No incluye versión impresa.",
            },
        },
        {
            "@type": "Question",
            name: "¿De cuántos capítulos consta el libro?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "16 capítulos organizados en 4 partes: La caída, Los centros, El descenso, y La salida. Cada capítulo incluye un 'Mapa Inverso' al final que convierte el patrón de autodestrucción descrito en un plan opuesto accionable.",
            },
        },
        {
            "@type": "Question",
            name: "¿Quién debería leer este libro?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Personas en consumo activo que sienten que no pueden parar, personas en recuperación que necesitan encontrarle sentido a su sobriedad, y familiares/parejas/amigos de alguien con adicción. No es un libro religioso, no es coaching, no es manual: es testimonio crudo escrito por alguien que estuvo 20+ años en la primera línea.",
            },
        },
    ],
};

export default function Libro() {
    return (
        <>
            <SEO
                title={`${LIBRO.title} · ${LIBRO.subtitle}`}
                description={`Libro digital de ${LIBRO.author}. 16 capítulos crudos sobre adicción, autodestrucción y sobriedad consciente. Descarga 100% gratis · sin costo, sin condiciones.`}
                path="/libro"
                keywords="el principio del mapa inverso, libro adicción Chile, libro sobriedad, Nelson González libro, libro gratis adicción, NiTanAdicto libro, recuperación adicción libro, autodestrucción libro, libro digital alcoholismo, manual sobriedad consciente, descargar libro adicción pdf gratis, libro recuperación drogas Chile"
                image={LIBRO.coverUrl}
                imageAlt={`Portada de '${LIBRO.title}' por ${LIBRO.author}`}
                type="book"
                jsonLd={[
                    productJsonLd,
                    bookJsonLd,
                    libroFaqJsonLd,
                    websiteJsonLd,
                    organizationJsonLd,
                    personJsonLd,
                ]}
            />

            {/* HERO */}
            <section className="grain relative" data-testid="libro-hero">
                <div className="hazard-stripes h-2 w-full" />
                <div className="container-x py-16 md:py-24">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-8"
                        data-testid="libro-back"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        Volver a El Mapa Inverso
                    </Link>

                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">El libro · expediente editorial</p>
                            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] mb-6">
                                <span className="text-[var(--paper-2)]">El </span>
                                <span className="text-[var(--danger)]">Principio</span>
                                <br />
                                del <span className="text-[var(--warning)]">Mapa Inverso</span>
                            </h1>
                            <p className="font-display italic text-2xl text-[var(--paper-2)] leading-[1.2] mb-6 max-w-2xl">
                                {LIBRO.subtitle}
                            </p>
                            <p className="font-mono text-sm text-[var(--ink-muted)] mb-8">
                                por <strong className="text-[var(--paper-2)]">{LIBRO.author}</strong> · {LIBRO.edition} · {LIBRO.language}
                            </p>

                            <div className="border border-dashed border-[var(--ink-dim)] p-6 mb-8 bg-[#0d0d0d]">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--warning)] mb-3">
                                    Sinopsis
                                </p>
                                <div className="font-mono text-sm text-[var(--paper)] leading-relaxed space-y-3 whitespace-pre-wrap">
                                    {LIBRO.intro.split("\n\n").map((p, i) => (
                                        <p key={i}>{p}</p>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <Link
                                    to="/descargar"
                                    className="btn-brutal btn-brutal-danger"
                                    data-testid="libro-cta-comprar"
                                >
                                    <Heart className="w-4 h-4" />
                                    Descárgalo gratis
                                </Link>
                                <Link
                                    to="/mapa-inverso"
                                    className="btn-brutal btn-brutal-ghost"
                                    data-testid="libro-cta-app"
                                >
                                    <Compass className="w-4 h-4" />
                                    Probar la app gratis
                                </Link>
                            </div>
                        </div>

                        <aside className="lg:col-span-5">
                            <div className="relative aspect-[2/3] max-w-xs mx-auto mb-6">
                                <div className="absolute inset-0 bg-[var(--danger)] translate-x-4 translate-y-4" />
                                <img
                                    src={LIBRO.coverUrl}
                                    alt={`Portada del libro ${LIBRO.title}`}
                                    className="relative w-full h-full object-cover border-2 border-[var(--paper-2)]"
                                    loading="eager"
                                    data-testid="libro-cover"
                                />
                                <Stamp size="md" rotation={12} color="warning" className="absolute -top-3 -right-3">
                                    Digital
                                </Stamp>
                            </div>

                            <div className="border border-[var(--line)] bg-[#0d0d0d] p-5">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-3">
                                    Ficha técnica
                                </p>
                                <dl className="space-y-2 font-mono text-xs">
                                    <Row k="Título" v={LIBRO.title} />
                                    <Row k="Autor" v={LIBRO.author} />
                                    <Row k="Edición" v={LIBRO.edition} />
                                    <Row k="Idioma" v={LIBRO.language} />
                                    <Row k="Formato" v={LIBRO.format} />
                                    <Row k="SKU" v={LIBRO.sku} />
                                    <Row
                                        k="Precio"
                                        v={
                                            <span className="text-[var(--warning)] font-bold">
                                                {LIBRO.priceLabel}
                                            </span>
                                        }
                                        highlight
                                    />
                                </dl>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            {/* ÍNDICE COMPLETO */}
            <section className="py-20 md:py-24 bg-[#0d0d0d]" data-testid="libro-toc">
                <div className="container-x">
                    <p className="eyebrow mb-4">Índice completo · 02</p>
                    <h2 className="font-display text-5xl md:text-6xl mb-12 leading-[0.95]">
                        16 capítulos.{" "}
                        <span className="text-[var(--danger)]">4 partes.</span>{" "}
                        <span className="font-hand text-3xl text-[var(--warning)] -rotate-2 inline-block">
                            Un solo mapa.
                        </span>
                    </h2>

                    <div className="space-y-12">
                        {LIBRO.chapters.map((part, idx) => (
                            <article key={part.part} data-testid={`toc-PARTE-${idx + 1}`}>
                                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--danger)] mb-4 pb-4 border-b border-[var(--line)]">
                                    {part.part}
                                </p>
                                <ol className="grid md:grid-cols-2 gap-3">
                                    {part.items.map((c) => (
                                        <li
                                            key={c.n}
                                            className="border border-[var(--line)] bg-[#0a0a0a] p-4 hover:border-[var(--warning)] transition-colors flex items-baseline gap-4"
                                        >
                                            <span className="font-display text-3xl text-[var(--ink-dim)] leading-none w-10 flex-shrink-0">
                                                {String(c.n).padStart(2, "0")}
                                            </span>
                                            <span className="font-mono text-sm text-[var(--paper-2)] leading-relaxed">
                                                {c.title}
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* QUOTES */}
            <section className="py-20 md:py-24 grain" data-testid="libro-quotes">
                <div className="container-x">
                    <p className="eyebrow mb-4">Fragmentos · 03</p>
                    <h2 className="font-display text-4xl md:text-5xl mb-12 max-w-3xl leading-[0.95]">
                        Pasajes que no se pueden leer rápido.
                    </h2>
                    <div className="space-y-10 max-w-3xl">
                        {LIBRO.quotes.map((q, i) => (
                            <blockquote
                                key={i}
                                className="relative pl-8 border-l-2 border-[var(--danger)]"
                                data-testid={`libro-quote-${i}`}
                            >
                                <Quote className="absolute -top-1 -left-3 w-6 h-6 text-[var(--danger)] bg-[var(--bg)]" />
                                <p className="font-display italic text-2xl md:text-3xl leading-[1.4] text-[var(--paper-2)]">
                                    &ldquo;{q}&rdquo;
                                </p>
                            </blockquote>
                        ))}
                    </div>
                </div>
            </section>

            {/* DELIVERABLES + COMPRA */}
            <section
                className="py-20 md:py-24 bg-[#0a0a0a] border-y-2 border-[var(--danger)]"
                data-testid="libro-purchase"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4">Qué recibes · 04</p>
                            <h2 className="font-display text-4xl md:text-5xl mb-6 leading-[0.95]">
                                Acceso digital inmediato.
                            </h2>
                            <ul className="space-y-3 mb-8">
                                {LIBRO.deliverables.map((d) => (
                                    <li
                                        key={d}
                                        className="font-mono text-sm text-[var(--paper)] flex items-start gap-3"
                                    >
                                        <CheckCircle2 className="w-5 h-5 text-[var(--warning)] flex-shrink-0 mt-0.5" />
                                        {d}
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/descargar"
                                className="btn-brutal btn-brutal-danger"
                                data-testid="libro-cta-buy-bottom"
                            >
                                <Heart className="w-4 h-4" />
                                Descárgalo ahora
                            </Link>
                        </div>
                        <aside className="lg:col-span-5 text-center md:text-right border-t lg:border-t-0 lg:border-l border-dashed border-[var(--ink-dim)] pt-8 lg:pt-0 lg:pl-12">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-2">
                                100% gratis · sin costo, sin condiciones
                            </p>
                            <p className="font-display text-5xl md:text-6xl text-[var(--paper-2)] leading-none mb-2">
                                Llévatelo<br />gratis.
                            </p>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mb-6 leading-relaxed">
                                Sin pago. Sin tarjeta.<br />
                                Solo tu email para enviarte el link.
                            </p>
                            <p className="font-hand text-2xl text-[var(--warning)] -rotate-1 inline-block">
                                Entrega inmediata por correo.
                            </p>
                        </aside>
                    </div>
                </div>
            </section>

            {/* WHO IS THIS FOR */}
            <section className="py-20 md:py-24 grain" data-testid="libro-forwhom">
                <div className="container-x">
                    <p className="eyebrow mb-4">¿Para quién? · 05</p>
                    <h2 className="font-display text-4xl md:text-5xl mb-10 leading-[0.95] max-w-3xl">
                        Si te reconoces, este libro es para ti.
                    </h2>
                    <ul className="space-y-5 max-w-3xl">
                        {LIBRO.forWhom.map((line, i) => (
                            <li
                                key={i}
                                className="pl-6 border-l-2 border-[var(--warning)] font-display italic text-xl text-[var(--paper-2)] leading-[1.4]"
                            >
                                &ldquo;{line}&rdquo;
                            </li>
                        ))}
                    </ul>

                    <div className="mt-16 max-w-2xl">
                        <ShareBar
                            url={`${SITE_URL}/libro`}
                            title={`${LIBRO.title} — ${LIBRO.author}`}
                            text="Para quien ama a alguien que se está perdiendo y ya no sabe si intentar salvarlo es un acto de amor o seguir financiando su caída. Lee este libro:"
                            hashtags={["MapaInverso", "Sobriedad", "NiTanAdicto"]}
                            source="libro_page"
                            variant="dark"
                        />
                    </div>
                </div>
            </section>

            {/* TESTIMONIOS */}
            <TestimoniosSection
                source="libro"
                limit={6}
                eyebrow="Testimonios · 06"
                title="Lo que dicen quienes lo leyeron"
                background="darker"
            />
        </>
    );
}

function Row({ k, v, highlight }) {
    return (
        <div className="flex justify-between gap-3 py-1 border-b border-dashed border-[var(--ink-dim)] last:border-0">
            <dt className="text-[var(--ink-muted)] uppercase tracking-[0.15em]">{k}</dt>
            <dd className={`text-right ${highlight ? "text-[var(--warning)] font-bold" : "text-[var(--paper-2)]"}`}>
                {v}
            </dd>
        </div>
    );
}
